import { Twitter } from 'blaze-ui/icons'
import { object, string } from 'yup'
import { FormSchema } from '../Wizard/Form/Types'
import { topic } from './shared/topic'
import { brandVoiceId, tone } from './shared/voiceTones'
import { avoid } from './shared/avoid'
import { callToAction } from './shared/callToAction'
import { promotion } from './shared/promotion'
import { project } from './shared/project'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { Tone } from 'components/DocRightRail/screens/AIAssistant/ToneSelect'
import { AlmanacFile } from 'types'
import { safify } from 'components/Wizards/schemas/utils/safify'
import { twitterExternalIntegrationId } from './shared/externalIntegrationId'
import { includeImages } from './shared/includeImages'
import { captionCount } from './shared/captionCount'
import { sourceMaterialIds } from './shared/sourceMaterials'
import { MultiStepSchema } from './Types'
import { brandVisualKitId } from './shared/brandVisualKit'
import { applyBrand } from './shared/applyBrand'
import { MediaType, mediaType } from './shared/mediaType'
import { brandKitId } from './shared/brandKitId'
import { language } from './shared/language'
import { TextChannels } from '../../../constants'

export enum TwitterPostLength {
  UpToOneHundredAndFortyCharacters = 'Up to 140 characters',
  BetweenOneHundredAndFortyAndTwoHundredAndEightyCharacters = '140-280 characters',
}

export type Values = {
  topic?: string
  caption_length?: string
  caption_count?: string
  filename?: string
  brand_voice_id?: number
  tone?: Array<Tone>
  language?: string
  project?: AlmanacFile
  source_material_ids?: number[]
  promotion?: string
  call_to_action?: string
  author_persona?: string
  product_description?: string
  avoid?: string
  additional_info?: string
  include_images?: boolean
  external_integration_id?: number
}

const meta = {
  type: GenerationType.TwitterPlanning,
  steps: ['Topic', 'Posts', 'Plan'],
  submitLabel: 'Generate Posts',
  icon: Twitter,
  previewUrl:
    'https://res.cloudinary.com/almanac/image/upload/v1710183792/Home%20Card%20Previews/guxh6oo1ezhztatbgnik.png',
}

const fields = {
  topic: topic.meta({ label: 'What would you like to write about?', generationType: meta.type }),
  filename: string().default('').meta({ hide: true, autoFill: true }).transform(safify),
  media_type: mediaType.default(MediaType.DocumentWithImages),
  project: project.meta({ hide: true, autoFill: true }),
  caption_length: string()
    .required()
    .oneOf([
      TwitterPostLength.UpToOneHundredAndFortyCharacters,
      TwitterPostLength.BetweenOneHundredAndFortyAndTwoHundredAndEightyCharacters,
    ])
    .default(TwitterPostLength.UpToOneHundredAndFortyCharacters)
    .meta({ label: 'How long should each post be?', autoFill: true })
    .transform(safify),
  caption_count: captionCount,
  tone,
  brand_voice_id: brandVoiceId,
  source_material_ids: sourceMaterialIds,
  brand_kit_id: brandKitId,
  include_images: includeImages.meta({ hide: true }),
  external_integration_id: twitterExternalIntegrationId,
  language,
  promotion: promotion.meta({ collapsible: true, autoFill: true }),
  call_to_action: callToAction.meta({ collapsible: true, autoFill: true }),
  avoid: avoid.meta({ collapsible: true, autoFill: true }),
}

export const TwitterPlanningSchema = object(fields).meta(meta) as FormSchema<GenerationType | ChannelType>

export const MultiStepTwitterPlanningSchema = {
  meta: {
    type: GenerationType.TwitterPlanning,
    channel: ChannelType.Tweets,
    title: TextChannels[GenerationType.TwitterPlanning].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183792/Home%20Card%20Previews/guxh6oo1ezhztatbgnik.png',
    icon: Twitter,
  },
  steps: [
    TwitterPlanningSchema.meta({ title: 'Topic', submitLabel: 'Generate Posts' }),
    object({}).meta({
      type: ChannelType.Tweets,
      title: 'Posts',
      submitLabel: 'Plan',
    }),
    object({
      brand_visual_kit_id: brandVisualKitId,
      apply_brand: applyBrand,
    }).meta({
      type: ChannelType.Tweets,
      title: 'Plan',
      submitLabel: 'Generate Posts',
    }),
  ],
} as MultiStepSchema
